import { useState, useEffect, useCallback } from 'react';
import {
	Nav,
	Tab,
	Card,
	Button,
	Form,
	Col,
	Container,
	Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
	FullCertificate,
	saveCertificate,
	calculatePallet,
	SelectDropdown,
} from '../../services/certificateService';
import NotificationClass from '../../typescript/classes/NotificationClass';
import { NotificationTypes } from '../../typescript/enums/enums';
import { t } from '../../services/translationService';
import { clear } from 'console';

declare global {
	interface Bokinfo {
		certificate: FullCertificate;
	}
}

export const EditCertificate = () => {
	const { certificate } = window.bokinfo;

	const { handleSubmit, formState, register, setValue, getValues, reset, watch }: any =
		useForm<FullCertificate>({
			mode: 'onBlur',
			defaultValues: window.bokinfo.certificate,
		});

	const [palletType, setPalletType] = useState(
		window.bokinfo.certificate.palletType || '10'
	);
	const { errors  } = formState;

  // Watch book fields for pallet calculation
  const bookWidth = watch('width');
  const bookHeight = watch('height');
  const bookBackwidth = watch('backwidth');
  const bookWeight = watch('weight');

  /**
   * Recalculates pallet dimensions from book dimensions and pallet type,
   * then updates related form fields.
   *
   * @param bookDimensions - Book height, width, backwidth and weight.
   */
  const applyPalletCalculation = useCallback(async (bookDimensions: any) => {
    const palletType = getValues('palletType');

    const pallet = await calculatePallet(
      palletType,
      bookDimensions.height,
      bookDimensions.width,
      bookDimensions.backwidth,
      bookDimensions.weight
    );

    if (pallet) {
      setValue('palletItemsPerLayer', pallet.palletItemsPerLayer);
      setValue('palletLayers', pallet.palletLayers);
      setValue('palletTotalHeight', pallet.palletTotalHeight);
      setValue('palletTotalItems', pallet.palletTotalItems);
      setValue('palletTotalWeight', pallet.palletTotalWeight);
    }
	}, [getValues, setValue]);

	useEffect(() => {
		(async () => {
			if (palletType) {
				setValue('palletType', palletType);
				const palletFields = getValues([
					'height',
					'width',
					'backwidth',
					'weight',
				]);

				await applyPalletCalculation({
					palletType,
					height: palletFields[0],
					width: palletFields[1],
					backwidth: palletFields[2],
					weight: palletFields[3],
				});
			}
		})();
		reset({}, { keepValues: true, keepIsValid: true });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [palletType, setValue, getValues, certificate, reset]);

/**
 * Debounces pallet calculation when book dimensions change.
 * Waits 500ms after the last change before applying recalculation,
 * and clears the timeout if values update again during the delay.
 */
useEffect(() => {
  const timeout = setTimeout(async () => {
    await applyPalletCalculation({
      height: bookHeight,
      width: bookWidth,
      backwidth: bookBackwidth,
      weight: bookWeight
    });
  }, 500);

  return () => clearTimeout(timeout);

}, [bookWidth, bookHeight, bookBackwidth, bookWeight, getValues, applyPalletCalculation]);


	const HandleCancel = () => {
		window.history.go(-1);
	};

	const dropdowns = window.bokinfo.certificate.dropDowns;

	const onSubmit = (values: any) => {
		saveCertificate(values).then((message: any) => {
			if (message.validation.valid) {
				setValue('id', message.certificateId);
				NotificationClass.createNotification({
					type: NotificationTypes.Success,
					message: 'Certifikatet uppdaterades',
				});
			} else {
				const valueMessages = Object.values(
					message.validation.invalidValueMessages || {}
				)
					.filter((msg: any) => msg && msg.trim() !== '')
					.map((msg: any) => msg.trim());

				NotificationClass.createNotification({
					type: NotificationTypes.Error,
					message:
						'Certifikatet kunde inte sparas: <br/>' +
						valueMessages.map((msg: any) => msg + '<br/>'),
					isHtml: true,
				});
			}
		});
	};

	const dropdownValues = (dropdown: SelectDropdown) => dropdown?.values &&
		Object.entries(dropdown.values).map(([key, label]) => (
			<option key={key} value={key}>{label}</option>
		));

	return (
		<Tab.Container defaultActiveKey="info">
			<Nav variant="tabs" role="tablist">
				<Nav.Item>
					<Nav.Link eventKey="info">{t('certifikatinfo')}</Nav.Link>
				</Nav.Item>
				<Nav.Item>
					<Nav.Link eventKey="properties">{t('egenskaper')}</Nav.Link>
				</Nav.Item>
				<Nav.Item>
					<Nav.Link eventKey="pallet">{t('pall')}</Nav.Link>
				</Nav.Item>
				<Nav.Item>
					<Nav.Link eventKey="contact">{t('kontaktInformation')}</Nav.Link>
				</Nav.Item>
			</Nav>
			{Object.keys(errors)?.length > 0 && (
				<div className="my-4">
					<div className="validation__error__block">
						<span className="validation__error__block__description">
							<i className="bi bi-exclamation-circle me-3" />
							{t('invalidCertificate')}
						</span>
						<ul className="validation__error__block__list">
							{Object.values(errors).map((e: any) => (
								<li>{e.message}</li>
							))}
						</ul>
					</div>
				</div>
			)}
			<Card className="mt-3 nopadding">
				<Card.Body className="pt-3">
					<Form id="certificateForm" onSubmit={handleSubmit(onSubmit)}>
						<Tab.Content>
							<Tab.Pane eventKey="info">
								<Container>
									<Row>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('titel')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('title', {
														required: `${t('titel')} ${t('isObligatory')}`,
													})}
													isInvalid={!!errors.title}
												/>
												{errors.title && (
													<Form.Control.Feedback type="invalid">
														{`${errors.title.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('glnSaljare')}*
												</Form.Label>
												<Form.Select
													isInvalid={!!errors.sellerGln}
													{...register('sellerGln', {
														required: `${t('glnSaljare')} ${t(
															'isObligatoryAlt'
														)}`,
													})}
												>
													{dropdowns?.glnSell &&
														dropdownValues(dropdowns.glnSell)}
												</Form.Select>
												{errors.sellerGln && (
													<Form.Control.Feedback type="invalid">
														{`${errors.sellerGln.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('glnKopare')}*
												</Form.Label>
												<Form.Select
													isInvalid={!!errors.buyerGln}
													{...register('buyerGln', {
														required: `${t('glnKopare')} ${t(
															'isObligatoryAlt'
														)}`,
													})}
												>
													{dropdowns?.glnBuy &&
														dropdownValues(dropdowns.glnBuy)}
												</Form.Select>
												{errors.buyerGln && (
													<Form.Control.Feedback type="invalid">
														{`${errors.buyerGln.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('gtin')}
												</Form.Label>
												<Form.Control
													readOnly
													plaintext
													{...register('gtin')}
												/>
											</Form.Group>
										</Col>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('skapad')}
												</Form.Label>
												<Form.Control
													readOnly
													plaintext
													{...register('created')}
												/>
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('andrad')}
												</Form.Label>
												<Form.Control
													readOnly
													plaintext
													{...register('modified')}
												/>
											</Form.Group>
										</Col>
									</Row>
								</Container>
							</Tab.Pane>
							<Tab.Pane eventKey="properties">
								<Container>
									<Row>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('produktklasskategori')}
												</Form.Label>
												<Form.Select {...register('productClassCategory')}>
													{dropdowns?.productClassCategory &&
														dropdownValues(dropdowns.productClassCategory)}
												</Form.Select>
												{errors.productClassCategory && (
													<Form.Control.Feedback type="invalid">
														{`${errors.productClassCategory.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('varubenamning')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('tradename', {
														required: `${t('varubenamning')} ${t(
															'isObligatory'
														)}`,
														maxLength: {
															value: 35,
															message: t('tradenameLengthErrorMessage'),
														},
													})}
													isInvalid={!!errors.tradename}
												/>
												{errors.tradename && (
													<Form.Control.Feedback type="invalid">
														{`${errors.tradename.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('varumarke')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('trademark_Author', {
														required: `${t('varumarke')} ${t(
															'isObligatoryAlt'
														)}`,
													})}
													isInvalid={!!errors.trademark_Author}
												/>
												{errors.trademark_Author && (
													<Form.Control.Feedback type="invalid">
														{`${errors.trademark_Author.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('hyllkantKvittotext')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('shelfEdge_ReceiptText', {
														required: `${t('hyllkantKvittotext')} ${t(
															'isObligatory'
														)}`,
														maxLength: {
															value: 18,
															message: t('shelfTextLengthErrorMessage'),
														},
													})}
													isInvalid={!!errors.shelfEdge_ReceiptText}
												/>
												{errors.shelfEdge_ReceiptText && (
													<Form.Control.Feedback type="invalid">
														{`${errors.shelfEdge_ReceiptText.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											{/* ImportClassification */}
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('importklassificering')}*
												</Form.Label>
												<Form.Control
													placeholder="Skriv..."
                          {...register('importClassification', {
                            required: `${t('importklassificering')} ${t('isObligatory')}`
                          })}
													isInvalid={!!errors.importClassification}
												/>
											</Form.Group>
										</Col>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('bredd')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('width', {
														required: `${t('bredd')} ${t('isObligatory')}`,
														min: { value: 1, message: t('widthErrorMessage') },
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.width}
												/>
												{errors.width && (
													<Form.Control.Feedback type="invalid">
														{`${errors.width.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('hojd')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('height', {
														required: `${t('hojd')} ${t('isObligatory')}`,
														min: { value: 1, message: t('heightErrorMessage') },
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.height}
												/>
												{errors.height && (
													<Form.Control.Feedback type="invalid">
														{`${errors.height.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('ryggbredd')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('backwidth', {
														required: `${t('ryggbredd')} ${t('isObligatory')}`,
														min: {
															value: 1,
															message: t('backWidthErrorMessage'),
														},
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.backwidth}
												/>
												{errors.backwidth && (
													<Form.Control.Feedback type="invalid">
														{`${errors.backwidth.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('vikt')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('weight', {
														required: `${t('vikt')} ${t('isObligatory')}`,
														min: { value: 1, message: t('weightErrorMessage') },
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.weight}
												/>
												{errors.weight && (
													<Form.Control.Feedback type="invalid">
														{`${errors.weight.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('moms')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('vat', {
														required: `${t('moms')} ${t('isObligatory')}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.vat}
												/>
												{errors.vat && (
													<Form.Control.Feedback type="invalid">
														{`${errors.vat.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('pris')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('price', {
														required: `${t('price')} ${t('isObligatory')}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.price}
												/>
												{errors.price && (
													<Form.Control.Feedback type="invalid">
														{`${errors.price.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
										</Col>
									</Row>
								</Container>
							</Tab.Pane>
							<Tab.Pane eventKey="pallet">
								<Container>
									<Row>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('palltyp')}
												</Form.Label>
												<Form.Select
													onChange={(e) => setPalletType(e.target.value)}
													value={palletType}
												>
													{dropdowns?.palletType &&
														dropdownValues(dropdowns.palletType)}
												</Form.Select>
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('gtin')}*
												</Form.Label>
												<Form.Control
													placeholder={t('skrivEllips')}
													{...register('gtinPallet', {
														required: `${t('gtin')} ${t('isObligatory')}`,
													})}
													isInvalid={!!errors.gtin}
												/>
												{errors.gtin && (
													<Form.Control.Feedback type="invalid">
														{`${errors.gtin.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
										</Col>
										<Col md={6}>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('antalLager')}*
												</Form.Label>
												<Form.Control
													{...register('palletLayers', {
														required: `${t('antalLager')} ${t('isObligatory')}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														}                            
													})}
													isInvalid={!!errors.palletLayers}
                          readOnly
												/>
												{errors.palletLayers && (
													<Form.Control.Feedback type="invalid">
														{`${errors.palletLayers.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('antalPerLager')}*
												</Form.Label>
												<Form.Control
													{...register('palletItemsPerLayer', {
														required: `${t('antalPerLager')} ${t(
															'isObligatory'
														)}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.palletItemsPerLayer}
                          readOnly
												/>
												{errors.palletItemsPerLayer && (
													<Form.Control.Feedback type="invalid">
														{`${errors.palletItemsPerLayer.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('totalVikt')}*
												</Form.Label>
												<Form.Control
													{...register('palletTotalWeight', {
														required: `${t('totalVikt')} ${t('isObligatory')}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.palletTotalWeight}
                          readOnly
												/>
												{errors.palletTotalWeight && (
													<Form.Control.Feedback type="invalid">
														{`${errors.palletTotalWeight.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('totalHojd')}*
												</Form.Label>
												<Form.Control
													{...register('palletTotalHeight', {
														required: `${t('totalHojd')} ${t('isObligatory')}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.palletTotalHeight}
                          readOnly
												/>
												{errors.palletTotalHeight && (
													<Form.Control.Feedback type="invalid">
														{`${errors.palletTotalHeight.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('totaltAntal')}*
												</Form.Label>
												<Form.Control
													{...register('palletTotalItems', {
														required: `${t('totaltAntal')} ${t(
															'isObligatory'
														)}`,
														pattern: {
															value: /^\d+$/,
															message: t('numbersOnly'),
														},
													})}
													isInvalid={!!errors.palletTotalItems}
                          readOnly
												/>
												{errors.palletTotalItems && (
													<Form.Control.Feedback type="invalid">
														{`${errors.palletTotalItems.message}`}
													</Form.Control.Feedback>
												)}
											</Form.Group>
										</Col>
									</Row>
								</Container>
							</Tab.Pane>
							<Tab.Pane eventKey="contact">
								<Container>
									<Row>
										<Col md={6}>
											{/* ContactName */}
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('kontaktNamn')}*
												</Form.Label>
												<Form.Control
													placeholder="Skriv..."
													{...register('contactName', {
                            required: `${t('kontaktNamn')} ${t('isObligatory')}`
                          })}
													isInvalid={!!errors.contactName}
												/>
											</Form.Group>

											{/* ContactAddress */}
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('kontaktAdress')}*
												</Form.Label>
												<Form.Control
                          as="textarea"
                          rows={3}
													placeholder="Skriv..."
													{...register('contactAddress', {
                            required: `${t('kontaktAdress')} ${t('isObligatory')}`
                          })}
													isInvalid={!!errors.contactAddress}
												/>
											</Form.Group>
										</Col>
										<Col md={6}>
											{/* ContactCommunicationChannelCode */}
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('kommunikationskanal')}*
												</Form.Label>
												<Form.Select
													{...register('contactCommunicationChannelCode', {
                            required: `${t('kommunikationskanal')} ${t('isObligatory')}`
                          })}
													isInvalid={!!errors.communicationChannel}
												>
													{dropdowns?.communicationChannel &&
														dropdownValues(dropdowns.communicationChannel)}
												</Form.Select>
											</Form.Group>

											{/* ContactCommunicationChannelAddress */}
											<Form.Group className="mb-3">
												<Form.Label className="mb-1 text-body">
													{t('kommunikationsadress')}*
												</Form.Label>
												<Form.Control
													placeholder="Skriv..."
													{...register('contactCommunicationChannelAddress', {
                            required: `${t('kommunikationsadress')} ${t('isObligatory')}`
                          })}
													isInvalid={
														!!errors.contactCommunicationChannelAddress
													}
												/>
											</Form.Group>
										</Col>
									</Row>
								</Container>
							</Tab.Pane>
						</Tab.Content>
					</Form>
				</Card.Body>
				<Card.Footer className="text-muted">
					<Button className="btn btn-secondary" onClick={HandleCancel}>
						{t('avbryt')}
					</Button>
					<Button variant="primary" form="certificateForm" type="submit">
						{t('sparaCertifikat')}
					</Button>
				</Card.Footer>
			</Card>
		</Tab.Container>
	);
};
